/**
 * Meta `budget_schedules` use a **UTC 15-minute** grid. Helpers here snap **event-wall**
 * `YYYY-MM-DDTHH:mm` strings to that grid where Meta requires it.
 *
 * **Plain event wall (no Meta snap):** use {@link formatPickerDayjsToEventWallMinute} for MUI
 * picker output — e.g. campaign flight dates must not ride the Meta grid.
 */

import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  META_BUDGET_SCHEDULE_INTERVAL_SEC,
  exclusiveNextLocalMidnightWallFromInclusiveDayStartWallMinute,
  snapUnixSecondsToMetaBudgetScheduleUtc,
} from "@common/utils/campaignBudgetMeta";
import {
  resolveEventWallTimezone,
  scheduleInputToEventWallMinute,
} from "./eventWallMinuteStrings";
import { wallMinuteToDayjsInEventZone } from "./wallMinutePickerDayjs";

dayjs.extend(utc);
dayjs.extend(timezone);

/** Minimum gap between phase start and end (matches Meta schedule quantum). */
export const META_PHASE_MIN_GAP_MINUTES =
  META_BUDGET_SCHEDULE_INTERVAL_SEC / 60;

const EVENT_WALL_MINUTE_FMT = "YYYY-MM-DDTHH:mm";

// --- Core: event-wall string ↔ Meta UTC grid ---------------------------------

/**
 * Snap an event-wall minute to Meta’s UTC 15-minute grid; return the same instant as
 * `YYYY-MM-DDTHH:mm` in the event zone.
 *
 * - `start`: floor in UTC (phase starts).
 * - `end`: ceil in UTC (phase ends / high-demand window ends).
 */
export function snapWallMinuteToMetaBudgetUtc(
  wallInput: string | undefined,
  eventTimezone: string,
  bound: "start" | "end"
): string {
  const wall = wallInput?.trim()
    ? scheduleInputToEventWallMinute(wallInput, eventTimezone)
    : "";
  if (!wall) return "";
  const tz = resolveEventWallTimezone(eventTimezone);
  const d = dayjs.tz(wall.slice(0, 16), tz);
  if (!d.isValid()) return "";
  const unixSeconds = Math.floor(d.valueOf() / 1000);
  const snapped = snapUnixSecondsToMetaBudgetScheduleUtc(
    unixSeconds,
    bound === "start" ? "floor" : "ceil"
  );
  return dayjs.unix(snapped).tz(tz).format(EVENT_WALL_MINUTE_FMT);
}

// --- MUI picker → event wall --------------------------------------------------

/** Interpret picker instant in `eventTimezone`; invalid picker → `null`. */
function pickerInstantInEventZone(
  value: Dayjs | null,
  eventTimezone: string
): { tz: string; z: Dayjs } | null {
  if (!value?.isValid()) return null;
  const tz = resolveEventWallTimezone(eventTimezone);
  const z = dayjs(value.valueOf()).tz(tz);
  return { tz, z };
}

/**
 * Picker `onChange` → event wall `YYYY-MM-DDTHH:mm` (minute precision, **no** Meta snap).
 * Use for campaign flight; phased rows use {@link snapPickerDayjsToWallMinute}.
 */
export function formatPickerDayjsToEventWallMinute(
  value: Dayjs | null,
  eventTimezone: string
): string {
  const p = pickerInstantInEventZone(value, eventTimezone);
  return p ? p.z.format(EVENT_WALL_MINUTE_FMT) : "";
}

/** Picker → event wall, then snap to Meta grid (phased datetime segments). */
export function snapPickerDayjsToWallMinute(
  value: Dayjs | null,
  eventTimezone: string,
  bound: "start" | "end"
): string {
  const p = pickerInstantInEventZone(value, eventTimezone);
  if (!p) return "";
  const wall = p.z.format(EVENT_WALL_MINUTE_FMT);
  return snapWallMinuteToMetaBudgetUtc(wall, p.tz, bound);
}

// --- Scheduled phases: user picks calendar days -------------------------------

/**
 * Phase start = start of selected local day (Meta-snapped), optionally raised to the campaign
 * flight start so a scheduled phase cannot begin before the ad run.
 */
export function snapPickerDayjsToScheduledPhaseDayStartWallMinute(
  value: Dayjs | null,
  eventTimezone: string,
  campaignFlightStart?: Dayjs | null
): string {
  const p = pickerInstantInEventZone(value, eventTimezone);
  if (!p) return "";
  let z = p.z.startOf("day");
  if (campaignFlightStart?.isValid()) {
    const flightAtEvent = dayjs(campaignFlightStart.valueOf()).tz(p.tz);
    if (flightAtEvent.isAfter(z)) {
      z = flightAtEvent;
    }
  }
  const wall = z.format(EVENT_WALL_MINUTE_FMT);
  return snapWallMinuteToMetaBudgetUtc(wall, p.tz, "start");
}

/**
 * Inclusive last calendar day → exclusive local next midnight (Meta-snapped), optionally capped
 * at campaign flight end so a phase cannot end after the ad run.
 */
export function snapPickerDayjsToScheduledPhaseInclusiveEndDayWallMinute(
  value: Dayjs | null,
  eventTimezone: string,
  campaignFlightEnd?: Dayjs | null
): string {
  const p = pickerInstantInEventZone(value, eventTimezone);
  if (!p) return "";
  const inclusiveWall = p.z.startOf("day").format(EVENT_WALL_MINUTE_FMT);
  const exclusiveWall =
    exclusiveNextLocalMidnightWallFromInclusiveDayStartWallMinute(
      inclusiveWall,
      p.tz
    );
  if (!exclusiveWall) return "";
  if (!campaignFlightEnd?.isValid()) {
    return snapWallMinuteToMetaBudgetUtc(exclusiveWall, p.tz, "start");
  }
  const exclusiveD = dayjs.tz(exclusiveWall.slice(0, 16), p.tz);
  if (!exclusiveD.isValid()) {
    return snapWallMinuteToMetaBudgetUtc(exclusiveWall, p.tz, "start");
  }
  const endWallRaw = dayjs(campaignFlightEnd.valueOf())
    .tz(p.tz)
    .format(EVENT_WALL_MINUTE_FMT);
  const endD = dayjs.tz(endWallRaw.slice(0, 16), p.tz);
  if (!endD.isValid()) {
    return snapWallMinuteToMetaBudgetUtc(exclusiveWall, p.tz, "start");
  }
  if (endD.isBefore(exclusiveD)) {
    return snapWallMinuteToMetaBudgetUtc(endWallRaw, p.tz, "end");
  }
  return snapWallMinuteToMetaBudgetUtc(exclusiveWall, p.tz, "start");
}

// --- Display: inclusive “last day” for review / pickers -----------------------

function isLocalMidnight(d: Dayjs): boolean {
  return (
    d.hour() === 0 &&
    d.minute() === 0 &&
    d.second() === 0 &&
    d.millisecond() === 0
  );
}

/**
 * Stored `time_end` is usually exclusive (next midnight). Map that to the **last calendar day**
 * the user considers included (for labels and date-only pickers).
 */
export function inclusiveEndCalendarDayFromPhaseEndWall(
  time_end: string | undefined,
  eventTimezone: string
): Dayjs | null {
  const raw = time_end?.trim();
  if (!raw) return null;
  const tz = resolveEventWallTimezone(eventTimezone);
  const snappedEndWall = snapWallMinuteToMetaBudgetUtc(raw, tz, "end");
  const d = wallMinuteToDayjsInEventZone(snappedEndWall || raw, tz);
  if (!d?.isValid()) return null;
  const z = d.tz(tz);
  if (isLocalMidnight(z)) {
    return z.subtract(1, "day").startOf("day");
  }
  return z.startOf("day");
}

// --- Wall arithmetic (still on Meta grid) ------------------------------------

/** Add minutes in event zone, then snap to Meta grid. */
export function addWallMinutesThenSnapWallMinute(
  wall: string,
  eventTimezone: string,
  minutes: number,
  snapBound: "start" | "end"
): string {
  const tz = resolveEventWallTimezone(eventTimezone);
  const base = scheduleInputToEventWallMinute(wall, eventTimezone);
  if (!base) return "";
  const d = dayjs.tz(base.slice(0, 16), tz);
  if (!d.isValid()) return "";
  const moved = d.add(minutes, "minute");
  const raw = moved.tz(tz).format(EVENT_WALL_MINUTE_FMT);
  return snapWallMinuteToMetaBudgetUtc(raw, tz, snapBound);
}
